import React, { ReactElement, useMemo } from 'react';
import { Col, FlexboxGrid, Row, Text } from 'rsuite';
import { convertNumberToCurrency } from '../../../common/utils';
import { TaxableReceipt } from 'src/types/tax';
import { TaxConfigurationInformation } from 'src/types/configuration';
import LabelWithInfoIcon from '../../common/LabelWithInfoIcon';

interface TaxableReceiptsBreakdownProps {
  configuration?: TaxConfigurationInformation;
  dark?: boolean;
  receipt: TaxableReceipt;
  testId?: string;
}

export const TaxableReceiptsBreakdown = ({
  configuration,
  dark,
  receipt: { accruedInterest, latePenalties, total, totDue, credit },
  testId,
}: TaxableReceiptsBreakdownProps): ReactElement => {
  const dueLabel = useMemo(() => {
    const breakdown = configuration?.breakdown.find(
      (element) => element.key === 'totDue'
    );
    return {
      label: breakdown?.label || 'Due',
      infoText: breakdown?.infoText,
    };
  }, [configuration]);

  const penaltiesLabel = useMemo(() => {
    const breakdown = configuration?.breakdown.find(
      (element) => element.key === 'latePenalties'
    );
    return {
      label: breakdown?.label || 'Penalties',
      infoText: breakdown?.infoText,
    };
  }, [configuration]);

  const interestLabel = useMemo(() => {
    const breakdown = configuration?.breakdown.find(
      (element) => element.key === 'accruedInterest'
    );
    return {
      label: breakdown?.label || 'Interest',
      infoText: breakdown?.infoText,
    };
  }, [configuration]);

  const taxDiscountLabel = useMemo(() => {
    const breakdown = configuration?.breakdown.find(
      (element) => element.key === 'credit'
    );
    return {
      label: breakdown?.label || 'Tax Discount',
      infoText: breakdown?.infoText,
    };
  }, [configuration]);

  return (
    <FlexboxGrid
      data-testid={testId}
      style={{
        background: dark ? '#f5f5f5' : '#fff',
        padding: '8px 0 8px 8px',
      }}
    >
      <FlexboxGrid.Item
        colspan={24}
        as={Row}
      >
        <FlexboxGrid style={{ fontSize: '14px', fontWeight: 'bold' }}>
          <FlexboxGrid.Item
            colspan={20}
            as={Col}
            style={{ paddingBottom: '4px' }}
          >
            <LabelWithInfoIcon
              label={`<b>${configuration?.label}</b>` || ''}
              infoText={configuration?.infoText}
            />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item
            colspan={4}
            as={Col}
          >
            <Text style={{ textAlign: 'right' }}>
              {convertNumberToCurrency(total)}
            </Text>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        colspan={24}
        as={Row}
      >
        <FlexboxGrid>
          <FlexboxGrid.Item
            colspan={20}
            as={Col}
          >
            <LabelWithInfoIcon
              label={dueLabel.label}
              infoText={dueLabel.infoText}
              className="pl-4"
            />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item
            colspan={4}
            as={Col}
          >
            <Text style={{ textAlign: 'right' }}>
              {convertNumberToCurrency(totDue)}
            </Text>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        colspan={24}
        as={Row}
      >
        {(!!credit || credit === 0) && (
          <FlexboxGrid>
            <FlexboxGrid.Item
              colspan={20}
              as={Col}
            >
              <LabelWithInfoIcon
                label={taxDiscountLabel.label}
                infoText={taxDiscountLabel.infoText}
                className="pl-4"
              />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              colspan={4}
              as={Col}
            >
              <Text style={{ textAlign: 'right' }}>
                {convertNumberToCurrency(credit)}
              </Text>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        )}
        <FlexboxGrid>
          <FlexboxGrid.Item
            colspan={20}
            as={Col}
          >
            <LabelWithInfoIcon
              label={penaltiesLabel.label}
              infoText={penaltiesLabel.infoText}
              className="pl-4"
            />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item
            colspan={4}
            as={Col}
          >
            <Text style={{ textAlign: 'right' }}>
              {convertNumberToCurrency(latePenalties)}
            </Text>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        colspan={24}
        as={Row}
      >
        <FlexboxGrid>
          <FlexboxGrid.Item
            colspan={20}
            as={Col}
          >
            <LabelWithInfoIcon
              label={interestLabel.label}
              infoText={interestLabel.infoText}
              className="pl-4"
            />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item
            colspan={4}
            as={Col}
          >
            <Text style={{ textAlign: 'right' }}>
              {convertNumberToCurrency(accruedInterest)}
            </Text>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};
